import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  ApartmentOutlined as CustomerIcon,
  AutorenewRounded as RenewalIcon,
  CategoryOutlined as TypeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FileDownloadOutlined as ExportIcon,
  NoteAddOutlined as AddContractIcon,
  PersonOutline as ManagerIcon,
  SettingsOutlined as ValuesIcon,
  HourglassBottomRounded as ExpiringIcon,
  WorkspacesOutlined as ClusterIcon,
} from '@mui/icons-material';
import PageHeader, { PageContainer } from '../components/PageHeader';
import MultiSelectFilter, { barButtonSx, SearchFilter, TogglePill } from '../components/monthly/MultiSelectFilter';
import { useFillViewportHeight } from '../components/monthly/tableStyles';
import ContractPanel, { ExpiryHint } from '../components/contracts/ContractPanel';
import ContractValuesDialog from '../components/contracts/ContractValuesDialog';
import { GREEN, GREEN_SOFT, INK, LINE, MUTED } from '../components/home/homeTheme';
import { useCurrentUser } from '../hooks/useCurrentUser';
import {
  aclParams,
  daysToEnd,
  englishLabel,
  exportContracts,
  fetchActiveLookups,
  fetchCanManageLookups,
  fetchContractsBundle,
  formatMoney,
  lookupLabel,
  MONTHLY_RATE_VALUE,
} from '../utils/contractsService';
import { parseLocalDate } from '../utils/dateHelpers';

const PAGE_SIZE = 100;
const EMPTY = [];
const EXPIRING_DAYS = 90;
const num = (n) => Number(n || 0).toLocaleString('en-GB');
const fmtDay = (v) => (v ? parseLocalDate(String(v).slice(0, 10)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const headSx = { fontSize: 12, fontWeight: 700, color: MUTED, bgcolor: 'var(--rt-paper)', py: 1, borderBottom: `1px solid ${LINE}`, whiteSpace: 'nowrap' };
const cellSx = { fontSize: 13, color: INK, py: 0.9, borderBottom: `1px solid ${LINE}` };
const isExpiring = (c) => {
  const d = daysToEnd(c.scadenza_contratto);
  return d != null && d >= 0 && d <= EXPIRING_DAYS;
};

/**
 * Contracts V2: elenco dei contratti con filtri a pillola, scadenze in evidenza e pannello laterale
 * (dettaglio, modifica, creazione, eliminazione). Il contratto aperto è nell'URL (?contract=ID, ?new=1).
 */
export default function ContractsV2() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const openId = params.get('contract');
  const creating = params.get('new') === '1';
  const { userId, userEmail, backendUser } = useCurrentUser();
  const acl = useMemo(() => aclParams({ userEmail, userId, roleId: backendUser?.role_id }), [userEmail, userId, backendUser?.role_id]);

  const [refreshToken, setRefreshToken] = useState(0);
  const [data, setData] = useState({ contracts: EMPTY, customers: EMPTY, serviceManagers: EMPTY, projects: EMPTY, lookups: { cluster: EMPTY, type: EMPTY, renewal: EMPTY } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [valuesOpen, setValuesOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchContractsBundle()
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);
  useEffect(() => {
    let cancelled = false;
    fetchCanManageLookups(acl).then((v) => !cancelled && setCanManage(v));
    return () => {
      cancelled = true;
    };
  }, [acl]);

  const { contracts, customers, serviceManagers, projects, lookups } = data;
  const label = {
    cluster: (c) => lookupLabel(lookups.cluster, c.cluster),
    type: (c) => lookupLabel(lookups.type, c.tipologia_servizio),
    renewal: (c) => englishLabel(lookupLabel(lookups.renewal, c.stato_rinnovo)),
  };

  // ---- Filtri ----
  const [search, setSearch] = useState('');
  const [customerFilter, setCustomerFilter] = useState(EMPTY);
  const [clusterFilter, setClusterFilter] = useState(EMPTY);
  const [typeFilter, setTypeFilter] = useState(EMPTY);
  const [renewalFilter, setRenewalFilter] = useState(EMPTY);
  const [managerFilter, setManagerFilter] = useState(EMPTY);
  const [onlyExpiring, setOnlyExpiring] = useState(false);
  const [sort, setSort] = useState({ key: 'end', dir: 'asc' });
  const [page, setPage] = useState(0);

  // Opzioni dei filtri: solo i valori presenti nei contratti ("none" = non impostato)
  const optionsOf = (getValue, getName) => {
    const seen = new Map();
    contracts.forEach((c) => {
      const v = getValue(c) || 'none';
      if (!seen.has(v)) seen.set(v, v === 'none' ? 'Not set' : getName(c) || v);
    });
    return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => (a.id === 'none') - (b.id === 'none') || a.name.localeCompare(b.name));
  };
  const customerOptions = useMemo(() => optionsOf((c) => (c.customer_id != null ? String(c.customer_id) : ''), (c) => c.customer_name), [contracts]); // eslint-disable-line react-hooks/exhaustive-deps
  const clusterOptions = useMemo(() => optionsOf((c) => c.cluster, label.cluster), [contracts, lookups]); // eslint-disable-line react-hooks/exhaustive-deps
  const typeOptions = useMemo(() => optionsOf((c) => c.tipologia_servizio, label.type), [contracts, lookups]); // eslint-disable-line react-hooks/exhaustive-deps
  const renewalOptions = useMemo(() => optionsOf((c) => c.stato_rinnovo, label.renewal), [contracts, lookups]); // eslint-disable-line react-hooks/exhaustive-deps
  const managerOptions = useMemo(() => optionsOf((c) => c.service_manager, (c) => c.service_manager), [contracts]); // eslint-disable-line react-hooks/exhaustive-deps

  const expiringCount = useMemo(() => contracts.filter(isExpiring).length, [contracts]);
  const expiredCount = useMemo(() => contracts.filter((c) => (daysToEnd(c.scadenza_contratto) ?? 0) < 0).length, [contracts]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const inFilter = (filter, value) => !filter.length || filter.includes(value || 'none');
    const list = contracts.filter((c) => {
      if (!inFilter(customerFilter, c.customer_id != null ? String(c.customer_id) : '')) return false;
      if (!inFilter(clusterFilter, c.cluster)) return false;
      if (!inFilter(typeFilter, c.tipologia_servizio)) return false;
      if (!inFilter(renewalFilter, c.stato_rinnovo)) return false;
      if (!inFilter(managerFilter, c.service_manager)) return false;
      if (onlyExpiring && !isExpiring(c)) return false;
      if (!q) return true;
      return [c.customer_name, c.yubiq_code, c.description, c.client_manager, c.service_manager, c.note, String(c.id)].some((v) => String(v || '').toLowerCase().includes(q));
    });
    const text = (get) => (a, b) => String(get(a) || '~').localeCompare(String(get(b) || '~'));
    const numeric = (get) => (a, b) => (get(a) ?? -Infinity) - (get(b) ?? -Infinity);
    const sorters = {
      customer: text((c) => c.customer_name),
      cluster: text(label.cluster),
      type: text(label.type),
      manager: text((c) => c.service_manager),
      current: numeric((c) => (c.tipologia_servizio === MONTHLY_RATE_VALUE && c.canone_mensile != null ? Number(c.canone_mensile) : null)),
      renewalFee: numeric((c) => (c.importo_rinnovo != null ? Number(c.importo_rinnovo) : null)),
      renewal: text(label.renewal),
      // Senza data di fine in fondo
      end: (a, b) => String(a.scadenza_contratto || '9999').localeCompare(String(b.scadenza_contratto || '9999')),
    };
    list.sort((a, b) => sorters[sort.key](a, b) || sorters.customer(a, b));
    if (sort.dir === 'desc') list.reverse();
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts, lookups, search, customerFilter, clusterFilter, typeFilter, renewalFilter, managerFilter, onlyExpiring, sort]);

  useEffect(() => setPage(0), [rows.length, sort]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const anyFilter = search || customerFilter.length || clusterFilter.length || typeFilter.length || renewalFilter.length || managerFilter.length || onlyExpiring;

  // ---- Pannello ----
  const openContract = contracts.find((c) => String(c.id) === String(openId)) || null;
  const showContract = (id) => setParams({ contract: String(id) });
  const closePanel = () => setParams({});
  const afterChange = (message, id) => {
    setSnack(message);
    setRefreshToken((t) => t + 1);
    if (id) showContract(id);
    else closePanel();
  };
  const closeValues = async () => {
    setValuesOpen(false);
    const fresh = await fetchActiveLookups();
    setData((d) => ({ ...d, lookups: fresh }));
  };

  const tableRef = useRef(null);
  const fillHeight = useFillViewportHeight(tableRef, [loading, error]);

  const sortHeader = (key, text, align) => (
    <TableCell sx={{ ...headSx, textAlign: align }} sortDirection={sort.key === key ? sort.dir : false}>
      <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : 'asc'} onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}>
        {text}
      </TableSortLabel>
    </TableCell>
  );

  const subtitle = loading
    ? 'Loading…'
    : [`${num(contracts.length)} contract${contracts.length === 1 ? '' : 's'}`, expiringCount && `${num(expiringCount)} expiring within ${EXPIRING_DAYS} days`, expiredCount && `${num(expiredCount)} expired`].filter(Boolean).join(' · ');

  return (
    <PageContainer>
      <PageHeader title="Contracts" subtitle={subtitle}>
        <Button variant="outlined" startIcon={<ExportIcon />} onClick={() => exportContracts(rows, lookups)} disabled={loading || !rows.length} sx={{ ...barButtonSx, height: 40 }}>
          Export{anyFilter ? ` (${num(rows.length)})` : ''}
        </Button>
        {canManage && (
          <Button variant="outlined" startIcon={<ValuesIcon />} onClick={() => setValuesOpen(true)} sx={{ ...barButtonSx, height: 40 }}>
            Contract values
          </Button>
        )}
        <Button
          variant="contained"
          color="success"
          startIcon={<AddContractIcon />}
          onClick={() => setParams({ new: '1' })}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', height: 40, bgcolor: GREEN }}
        >
          Add contract
        </Button>
      </PageHeader>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Filtri */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <SearchFilter value={search} onChange={setSearch} placeholder="Search customer, Yubiq code, managers, notes" width={320} />
          <MultiSelectFilter label="Customers" allLabel="All customers" icon={<CustomerIcon />} options={customerOptions} value={customerFilter} onChange={setCustomerFilter} width={220} />
          <MultiSelectFilter label="Clusters" allLabel="All clusters" icon={<ClusterIcon />} options={clusterOptions} value={clusterFilter} onChange={setClusterFilter} width={190} />
          <MultiSelectFilter label="Types" allLabel="All types" icon={<TypeIcon />} options={typeOptions} value={typeFilter} onChange={setTypeFilter} width={190} />
          <MultiSelectFilter label="Renewal statuses" allLabel="All renewal statuses" icon={<RenewalIcon />} options={renewalOptions} value={renewalFilter} onChange={setRenewalFilter} width={240} />
          <MultiSelectFilter label="Service managers" allLabel="All service managers" icon={<ManagerIcon />} options={managerOptions} value={managerFilter} onChange={setManagerFilter} width={240} />
          <TogglePill label={`Expiring within ${EXPIRING_DAYS} days (${num(expiringCount)})`} checked={onlyExpiring} onChange={setOnlyExpiring} icon={<ExpiringIcon />} />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box ref={tableRef} sx={{ border: `1px solid ${LINE}`, borderRadius: 3, bgcolor: 'var(--rt-paper)', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress color="success" />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: fillHeight ? fillHeight - 48 : 'calc(100vh - 260px)' }}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 1120 }}>
                  <colgroup>
                    <col />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 180 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 210 }} />
                    <col style={{ width: 140 }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      {sortHeader('customer', 'Customer')}
                      {sortHeader('cluster', 'Cluster')}
                      {sortHeader('type', 'Type')}
                      {sortHeader('manager', 'Service manager')}
                      {sortHeader('current', 'Current fee', 'right')}
                      {sortHeader('renewalFee', 'Renewal fee', 'right')}
                      {sortHeader('renewal', 'Renewal status')}
                      {sortHeader('end', 'End date')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.map((c) => {
                      const selected = String(c.id) === String(openId);
                      const monthlyRate = c.tipologia_servizio === MONTHLY_RATE_VALUE;
                      return (
                        <TableRow key={c.id} hover selected={selected} onClick={() => showContract(c.id)} sx={{ cursor: 'pointer', '&.Mui-selected, &.Mui-selected:hover': { bgcolor: GREEN_SOFT } }}>
                          <TableCell sx={cellSx}>
                            <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600 }} title={c.customer_name}>
                              {c.customer_name || '—'}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: 12, color: MUTED, fontFamily: 'monospace' }}>
                              {c.yubiq_code || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...cellSx, color: c.cluster ? INK : MUTED }}>{label.cluster(c) || '—'}</TableCell>
                          <TableCell sx={{ ...cellSx, color: c.tipologia_servizio ? INK : MUTED }}>
                            <Typography noWrap sx={{ fontSize: 13, color: 'inherit' }}>
                              {label.type(c) || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <Typography noWrap sx={{ fontSize: 13, color: c.service_manager ? INK : MUTED }}>
                              {c.service_manager || '—'}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: 12, color: MUTED }} title={c.client_manager || ''}>
                              {c.client_manager ? `Client: ${c.client_manager}` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...cellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: monthlyRate && c.canone_mensile != null ? INK : MUTED }}>
                            {monthlyRate && formatMoney(c.canone_mensile) ? `€ ${formatMoney(c.canone_mensile)}` : '—'}
                          </TableCell>
                          <TableCell sx={{ ...cellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: c.importo_rinnovo != null ? INK : MUTED }}>
                            {formatMoney(c.importo_rinnovo) ? `€ ${formatMoney(c.importo_rinnovo)}` : '—'}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <Typography noWrap sx={{ fontSize: 13, color: c.stato_rinnovo ? INK : MUTED }} title={label.renewal(c)}>
                              {label.renewal(c) || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                            <Typography sx={{ fontSize: 13, color: c.scadenza_contratto ? INK : MUTED, fontVariantNumeric: 'tabular-nums' }}>{fmtDay(c.scadenza_contratto)}</Typography>
                            <ExpiryHint endDate={c.scadenza_contratto} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!pageRows.length && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ ...cellSx, py: 4, textAlign: 'center', color: MUTED }}>
                          {contracts.length ? 'No contracts match these filters.' : 'No contracts yet.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderTop: `1px solid ${LINE}`, bgcolor: 'var(--rt-subtle-2)' }}>
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  {num(rows.length)} contract{rows.length === 1 ? '' : 's'}
                  {rows.length !== contracts.length ? ` of ${num(contracts.length)}` : ''} · click a row to open it
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 13, color: MUTED }}>
                    {rows.length ? `${num(page * PAGE_SIZE + 1)}–${num(Math.min(rows.length, (page + 1) * PAGE_SIZE))} of ${num(rows.length)}` : '0 of 0'}
                  </Typography>
                  <IconButton size="small" disabled={page === 0} onClick={() => setPage((n) => n - 1)} aria-label="Previous page">
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled={page >= pageCount - 1} onClick={() => setPage((n) => n + 1)} aria-label="Next page">
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <ContractPanel
        open={!!openContract || creating}
        contract={creating ? null : openContract}
        creating={creating}
        customers={customers}
        serviceManagers={serviceManagers}
        lookups={lookups}
        projects={projects}
        onClose={closePanel}
        onSaved={(id, message) => afterChange(message, id)}
        onDeleted={(message) => afterChange(message, null)}
        onOpenProject={(id) => navigate(`/projects/${id}`)}
      />
      {canManage && <ContractValuesDialog open={valuesOpen} acl={acl} onClose={closeValues} />}

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </PageContainer>
  );
}
